import { initializePrototypeRuntimeState, closeAllModals, setThemeMode, showView, setActiveTab, showArticle, openModal, applyInitialViewFromUrl, wirePopStateNavigation } from "./navigationController";
import { showTopicDetail } from "./topicDetailView";
import { configureTopicFetcher } from "./topicStore";
import { hideLoginState, wireSessionExpiredHandler } from "./authGateController";
import { loadViewData } from "./actionRoutes/contextLoaders";
import { auditPrototypeViewStructure } from "./viewStructureAudit";
import { enhanceProfileSettingsRows, nextMonthIso } from "./formProfileController";
import { exposeSmokeHelpers } from "./smokeHelpers";
import { hydratePrototypeRuntime, type PrototypeState } from "./prototypeHydration";
import { refreshHomeState } from "./homeStateController";
import { entitlementRefresh } from "./entitlementRefresh/entitlementRefresh";
import { attachInlineErrorClearer } from "./wizardValidation";
import type { PlanCode, PrototypeRuntime, Root } from "./prototypeTypes";
import { setRuntime } from "./runtimeAccessor";
import { attachPrototypeEventBindings } from './prototypeEventBindings';
import { createPrototypeState, ensureToastContainer } from './prototypeRuntimeFactory';
import { handlePrototypeDeepLinks } from './prototypeDeepLinks';
import { isSsrDirectMode, readPageModel } from './pageModelReader';
import { initializeModalAccessibility } from './modalAccessibility';
import { startAuthSessionSync } from './authSessionSync';
import { createNotificationCenterController } from './notificationCenterController';
import { getOrCreateMessagesPageController } from './messagesPageController';
import { renderKeywordCloud } from './keywordCloud';
import { hydrateSsrTopicPrimaryAction } from './topicDetailHydrator';
import { wireSsrArticleMedia } from './articleMediaWiring';
import { readThemeMode } from './themeBootstrapRuntime';
import { renderMermaidBlocks } from './markdownRenderer';
import { mountMobileBottomDockController } from './mobileBottomDockController';
import { primeSsrTopicReturnSnapshot } from './topicReturnSnapshot';
import { createAiCreateResumeController } from './aiCreateResumeController';
import { fetchAndRenderAiCourses, refreshAiCreateProgressSnapshot } from './aiCreateViewHydrator';
import { primeRefreshFirstFrame } from './refreshFirstFrame';
import { attachAiProgressIssueTooltipController } from './aiProgressIssueTooltipController';
import { destroyBrowseIndustryMenu, enableBrowseIndustryMenu } from './browseIndustryMenu';
import { attachAudioPlayer, detachAudioPlayer } from './mediaPlayerAudioControls';
import { detachMediaEndedCommands, wireMediaEndedCommands } from './mediaPlayerEndedControls';
import { mountMobileContextHeader } from './mobileContextHeader';
export type { PrototypeState } from "./prototypeHydration";
export function mountPrototypeController(root: HTMLElement) {
	enableBrowseIndustryMenu(root);
  const state = createPrototypeState();
  primeRefreshFirstFrame(root, state);
  const detachAuthSessionSync = startAuthSessionSync(() => window.location.reload());
  const notificationCenter = createNotificationCenterController(root, state, undefined, { deferInitialAuth: true });
  const aiCreateResume = createAiCreateResumeController(root, state, {
	refresh: (runtime, targetRoot, filter, search) => filter === 'inprogress'
		? refreshAiCreateProgressSnapshot(runtime, targetRoot, search)
		: fetchAndRenderAiCourses(runtime, targetRoot, filter, search),
  });
  const messagesPage = getOrCreateMessagesPageController(root, state.runtime.api, () => ({
    isLoggedIn: state.isLoggedIn,
    userId: state.isLoggedIn ? state.runtime.userId || null : null,
  }));
  ensureToastContainer();
  const detachAiProgressIssueTooltip = attachAiProgressIssueTooltipController(root);
  const detachEventBindings = attachPrototypeEventBindings(root, state);
  const mobileBottomDock = mountMobileBottomDockController(root);
	const mobileContextHeader = mountMobileContextHeader(root);
  const detachErrorClearer = attachInlineErrorClearer(root);
  setThemeMode(readThemeMode(), root, false);
  initializePrototypeRuntimeState(root);
  initializeModalAccessibility(root);
  (root as unknown as { __prototypeState: PrototypeState }).__prototypeState = state;
  setRuntime(root, state.runtime);
	// The audio bar is a root-owned surface. Bind it before any Home Recent
	// action can mint an audio token; article paints only refresh presentation.
	attachAudioPlayer(root, state.runtime.api, state.runtime.activeCourseId ?? '', state.runtime.activeChapterIndex ?? 1, state.runtime.currentPlan ?? 'free');
	wireMediaEndedCommands(root, state.runtime.api, state.runtime.currentPlan ?? 'free');
  wireSessionExpiredHandler(root, state);
  // Route one-Topic navigation reads through the API client for retry, timeout, AbortController and call logging.
  configureTopicFetcher((slugOrId) => state.runtime.api.topicNavigation(slugOrId));
  const preserveSsrView = initializeSsrDirectView(root, state);
  if (!preserveSsrView) applyInitialViewFromUrl(root, state, { hydrate: false });
  handlePrototypeDeepLinks(root, state);
  // Single-render: wire popstate so browser back/forward restores the right view.
  const detachPopState = wirePopStateNavigation(root);
  // bootstrapApp.ts seeds topicStore from #page-model before mounting.

  enhanceProfileSettingsRows(root);
	exposeSmokeHelpers(root, state.runtime, { isLoggedIn: state.isLoggedIn, currentPlan: state.effectiveAccessPlan }, {
    showView,
    setActiveTab,
    showTopicDetail,
    showArticle,
    openModal,
    closeAllModals,
    hideLoginState: (targetRoot: Root) => hideLoginState(targetRoot, state),
    refreshHomeState: (targetRoot: Root) => refreshHomeState(targetRoot, state),
    loadViewData: (view: string, runtime: PrototypeRuntime) => loadViewData(view, runtime, state, root),
    nextMonthIso,
    setCurrentPlan: (plan: PlanCode) => {
      state.currentPlan = plan;
		state.effectiveAccessPlan = plan;
		state.runtime.currentPlan = plan;
    },
  });
  void hydratePrototypeRuntime(root, state, { preserveSsrView }).then(() => {
	if (!preserveSsrView) return;
	const model = readPageModel();
	if (!model || model.pageType !== 'topic') return;
	primeSsrTopicReturnSnapshot(root, model, state.runtime, state.effectiveAccessPlan);
	const isCurrentSsrTopic = () => state.runtime.activeCourseId === model.topic.slug
		&& root.querySelector('#view-topic')?.classList.contains('active') === true;
	void import('./topicController').then(({ renderTopicChapters }) => {
		if (isCurrentSsrTopic()) return renderTopicChapters(root, model.topic.slug, state.effectiveAccessPlan, state.runtime, undefined, isCurrentSsrTopic);
	});
	const firstChapterIndex = model.chapters.map((chapter) => chapter.index).find((index) => Number.isInteger(index) && index > 0);
	if (!firstChapterIndex) return;
	void hydrateSsrTopicPrimaryAction(root, state.runtime.api, model.topic, state.effectiveAccessPlan, firstChapterIndex);
  });
  // Keep structural leaks visible to smoke tests and browser diagnostics.
  auditPrototypeViewStructure(root);

  // P1-11: 卸载时清理全部监听 + 权益刷新资源, 避免 SPA 重挂载泄漏。
  return () => {
    detachAuthSessionSync();
    detachAiProgressIssueTooltip();
    detachEventBindings();
		detachAudioPlayer(root);
		detachMediaEndedCommands(root);
		destroyBrowseIndustryMenu(root);
    mobileBottomDock.destroy();
		mobileContextHeader.destroy();
    detachErrorClearer();
    detachPopState();
    entitlementRefresh.destroy();
    notificationCenter.destroy();
    aiCreateResume.destroy();
    messagesPage.destroy();
  };
}

/** Seeds navigation state without repainting server-rendered Topic/Chapter DOM. */
export function initializeSsrDirectView(root: Root, state: PrototypeState): boolean {
  if (!isSsrDirectMode()) return false;
  const model = readPageModel();
  if (!model) return false;
  state.runtime.activeCourseId = model.topic.slug;
  if (model.pageType === 'chapter') {
    state.runtime.activeChapterIndex = model.chapter.index;
    state.runtime.chapterIdByIndex = {
      ...state.runtime.chapterIdByIndex,
      [model.topic.slug]: {
        ...(state.runtime.chapterIdByIndex?.[model.topic.slug] ?? {}),
        [model.chapter.index]: model.chapter.id,
      },
    };
    // Stamp __activeChapter so showPlayer() can populate the mini bar
    // title/topic immediately. Without this the SSR path skips
    // primeArticleShell (which sets this on the SPA showArticle path)
    // and the mini bar falls back to the literal "Chapter".
    (root as unknown as { __activeChapter?: { index: number; title: string; topicTitle: string; topicSlug: string; topicSubtitle: string } }).__activeChapter = {
      index: model.chapter.index, title: model.chapter.title,
      topicTitle: model.topic.title, topicSlug: model.topic.slug,
      topicSubtitle: model.topic.subtitle,
    };
	const cloud = root.querySelector<HTMLElement>('#chapter-keyword-cloud');
	const keywords = model.chapter.keywords;
	if (cloud && keywords.length) renderKeywordCloud(cloud, keywords);
	wireSsrArticleMedia(root, model);
	void renderMermaidBlocks(root).catch(() => undefined);
  }
  if (model.pageType === 'topic') {
	const cloud = root.querySelector<HTMLElement>('#topic-keyword-cloud');
	const keywords = model.topic.keywords;
	if (cloud && keywords.length) renderKeywordCloud(cloud, keywords);
	if (cloud && keywords.length === 0) {
		cloud.replaceChildren();
		cloud.classList.remove('kw-cloud-3d', 'kw-cloud--skeleton', 'topic-keyword-cloud--static');
		root.querySelector<HTMLElement>('#topic-outcomes-block')?.setAttribute('hidden', '');
	}
	primeSsrTopicReturnSnapshot(root, model, state.runtime, state.effectiveAccessPlan);
  }
  root.querySelector<HTMLElement>(model.pageType === 'chapter' ? '#view-article' : '#view-topic')?.classList.add('active');
  setActiveTab('home-browse', root);
  return true;
}
